import React, { useState, useEffect } from 'react';
import { settingsService, UserProfile } from '../../services/settingsService';

const CREDIT_LABELS: Record<string, string> = {
  iasc: 'Search Service (Input)',
  oasc: 'Search Service (Output)',
  ics: 'Page Scraping (Input)',
  ocs: 'Page Scraping (Output)',
  idg: 'Document Generation (Input)',
  odg: 'Document Generation (Output)',
  idp: 'Document Processor (Input)',
  odp: 'Document Processor (Output)',
  ijde: 'Profile Enhancer (Input)',
  ojde: 'Profile Enhancer (Output)',
  ichat: 'Chat/LLM (Input)',
  ochat: 'Chat/LLM (Output)',
  ipm: 'Profile Match (Input)',
  opm: 'Profile Match (Output)',
};

const CreditSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setError(null);
      const data = await settingsService.getProfile();
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load credit information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCreditStatus = (value: number) => {
    if (value < 0) return 'text-red-600';
    if (value < 1000) return 'text-orange-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jobesta-blue"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-jobesta p-6">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-red-800">{error || 'Failed to load credit information. Please try again.'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Credit Overview */}
      <div className="bg-white rounded-jobesta shadow-jobesta-card p-6 border border-jobesta-gray-40">
        <h2 className="text-lg font-semibold text-jobesta-gray-90 mb-4">Your Credits</h2>
        <p className="text-sm text-jobesta-gray-60 mb-6">
          View your available credits for each service. Credits are consumed when you use different features.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(CREDIT_LABELS).map(([key, label]) => {
            const value = (profile[key as keyof UserProfile] as number) ?? 0;
            return (
              <div
                key={key}
                className="flex justify-between items-center p-4 bg-jobesta-gray-10 rounded-jobesta border border-jobesta-gray-30"
              >
                <span className="text-sm font-medium text-jobesta-gray-80">{label}</span>
                <span className={`text-lg font-bold ${getCreditStatus(value)}`}>
                  {value.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Account Type */}
      <div className="bg-white rounded-jobesta shadow-jobesta-card p-6 border border-jobesta-gray-40">
        <h2 className="text-lg font-semibold text-jobesta-gray-90 mb-4">Account Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-jobesta-gray-60">Account Type:</span>
            <p className="text-base font-semibold text-jobesta-gray-90">
              {profile.type === 0 ? 'Free' : 'Premium'}
            </p>
          </div>
          <div>
            <span className="text-sm text-jobesta-gray-60">Expiry Date:</span>
            <p className="text-base font-semibold text-jobesta-gray-90">
              {new Date(profile.expiry * 1000).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-jobesta-gray-10 rounded-jobesta p-4 border border-jobesta-gray-30">
        <h3 className="text-sm font-semibold text-jobesta-gray-90 mb-2">Credit Status</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-600 rounded-full"></div>
            <span className="text-jobesta-gray-70">Healthy (≥ 1000)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-600 rounded-full"></div>
            <span className="text-jobesta-gray-70">Low (0-999)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-600 rounded-full"></div>
            <span className="text-jobesta-gray-70">Negative</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditSettings;
